/**
 * ShareModal - Modal for configuring conversation sharing settings.
 * Allows users to set access levels and copy share links.
 */

import { Check, Copy01 } from '@untitledui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { RadioButton, RadioGroup } from '@/lib/base/radio-buttons';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { shareService } from '@/lib/share/service';
import type { AccessLevel } from '@/types/share';
import { cx } from '@/utils/cx';

export type ShareableObjectType =
  | 'conversation'
  | 'dynamic_view'
  | 'report'
  | 'dashboard';

export interface ShareModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Organization ID */
  orgId: number;
  /** Type of object to share */
  objectType: ShareableObjectType;
  /**
   * ID of the object to share. Numeric for conversations/dynamic views/
   * dashboards; reports use their CRM entity_id string.
   */
  objectId: number | string;
  /** Title of the object for display */
  objectTitle: string;
  /** Whether current user is the creator of the object */
  isCreator: boolean;
}

type AccessOption = AccessLevel | 'private';

interface AccessLevelOption {
  value: AccessOption;
  label: string;
  description: string;
}

const ACCESS_LEVEL_OPTIONS: AccessLevelOption[] = [
  {
    value: 'private',
    label: 'Private',
    description: 'Only you can access',
  },
  {
    value: 'org',
    label: 'Organization members only',
    description: 'Anyone in your organization',
  },
  {
    value: 'public',
    label: 'Anyone with the link',
    description: 'No login required',
  },
];

export function ShareModal({
  isOpen,
  onClose,
  orgId,
  objectType,
  objectId,
  objectTitle,
  isCreator,
}: ShareModalProps) {
  const { isDark } = useTheme();
  const [accessLevel, setAccessLevel] = useState<AccessOption>('private');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const loadShareSettings = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    // Clear any stale save error so a prior 'Failed to update share settings'
    // failure doesn't reappear when the modal is reopened (it can stay mounted
    // between visibility toggles).
    setError(null);
    try {
      const settings = await shareService.getShareSettings(
        orgId,
        objectType,
        objectId
      );
      if (settings && settings.token) {
        setAccessLevel(settings.access_level ?? 'private');
        setShareUrl(settings.url);
      } else {
        setAccessLevel('private');
        setShareUrl(null);
      }
    } catch (err) {
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  }, [orgId, objectType, objectId]);

  // Load current share settings when modal opens
  useEffect(() => {
    if (isOpen) {
      loadShareSettings();
    }
  }, [isOpen, loadShareSettings]);

  const handleAccessLevelChange = async (newLevel: AccessOption) => {
    if (!isCreator || isSaving) {
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (newLevel === 'private') {
        await shareService.deleteShare(orgId, objectType, objectId);
        setShareUrl(null);
      } else {
        const result = await shareService.createOrUpdateShare(
          orgId,
          objectType,
          objectId,
          newLevel
        );
        setShareUrl(result.url);
      }
      setAccessLevel(newLevel);
    } catch {
      setError("We couldn't update share settings. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopy = async () => {
    if (shareUrl) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
      } catch {
        // Fallback: select the input text
        const input =
          document.querySelector<HTMLInputElement>('[data-share-url]');
        if (input) {
          input.select();
        }
      }
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Truncate title if too long
  const displayTitle =
    objectTitle.length > 40 ? `${objectTitle.slice(0, 40)}…` : objectTitle;

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          handleClose();
        }
      }}
      isDismissable={!isSaving}
      className={isDark ? 'dark-mode' : undefined}
      data-theme={isDark ? 'dark' : 'light'}
    >
      <Modal className='max-w-md'>
        <Dialog title={`Share "${displayTitle}"`}>
          {/* No footer region, so the body carries the modal's bottom inset
              (PADDING CONTRACT in lib/base/overlays/modal.tsx). */}
          <div className='pb-6'>
            {/* Content */}
            {isLoading && (
              <>
                <div className='mb-6'>
                  {/* Skeleton for "Who can access this?" label */}
                  <div className='h-4 w-32 bg-bg-secondary rounded animate-pulse mb-3' />
                  {/* Skeleton for radio options - matches RadioGroup gap-4 */}
                  <div className='flex flex-col gap-4'>
                    {[1, 2, 3].map(i => (
                      <div key={i} className='flex items-start gap-2'>
                        <div className='w-4 h-4 rounded-full bg-bg-secondary animate-pulse shrink-0 mt-0.5' />
                        <div className='flex flex-col'>
                          <div className='h-4 w-40 bg-bg-secondary rounded animate-pulse' />
                          <div className='h-4 w-28 bg-bg-secondary rounded animate-pulse mt-1' />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Skeleton for share URL input - reserve space */}
                <div className='mb-4'>
                  <div className='flex gap-2'>
                    <div className='flex-1 h-10 bg-bg-secondary rounded-lg animate-pulse' />
                    <div className='w-20 h-10 bg-bg-secondary rounded-lg animate-pulse' />
                  </div>
                </div>
              </>
            )}
            {!isLoading && loadError != null && (
              <QueryErrorState
                error={loadError}
                label="We couldn't load share settings"
                onRetry={loadShareSettings}
              />
            )}
            {!isLoading && loadError == null && (
              <>
                {/* Access level options */}
                <div className='mb-6'>
                  <p className='text-sm font-medium text-fg-secondary mb-3'>
                    Who can access this?
                  </p>
                  <RadioGroup
                    value={accessLevel}
                    onChange={value =>
                      handleAccessLevelChange(value as AccessOption)
                    }
                    isDisabled={!isCreator || isSaving}
                    aria-label='Access level'
                  >
                    {ACCESS_LEVEL_OPTIONS.map(option => (
                      <RadioButton
                        key={option.value}
                        value={option.value}
                        label={option.label}
                        hint={option.description}
                        aria-label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </div>

                {error && (
                  <div className='mb-4 text-sm text-error-primary'>{error}</div>
                )}

                {/* Share URL */}
                {shareUrl && (
                  <div className='mb-4'>
                    <div className='flex gap-2'>
                      <input
                        type='text'
                        readOnly
                        value={shareUrl}
                        data-share-url
                        className={cx(
                          'flex-1 px-3 py-2 text-sm rounded-lg border',
                          'bg-bg-secondary border-primary text-fg-primary',
                          'outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2'
                        )}
                      />
                      <Button
                        color='secondary'
                        size='md'
                        onClick={handleCopy}
                        aria-label={copied ? 'Copied' : 'Copy'}
                        iconLeading={copied ? Check : Copy01}
                      >
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Non-creator notice */}
                {!isCreator && (
                  <div className='mt-4 p-3 rounded-lg bg-bg-secondary'>
                    <p className='text-sm text-fg-secondary'>
                      Only the creator can change sharing settings.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
